import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { tenantService } from "../Api/Services/tenantService";
import { tenantAboutService } from "../Api/Services/tenantAboutService";
import { tenantContactService } from "../Api/Services/tenantContactService";
import { tenantBranchService } from "../Api/Services/tenantBranchService";
import { tenantPreferenceService } from "../Api/Services/tenantPreferenceService";
import { tenantShippingProviderConfigService } from "../Api/Services/tenantShippingProviderConfigService";
import { tenantTechnicalAssistanceService } from "../Api/Services/tenantTechnicalAssistanceService";
import { productCategoryService } from "../Api/Services/productCategoryService";
import { Tenant, UpdateTenant } from "../Models/Tenant";
import { TenantAbout, UpdateTenantAbout } from "../Models/TenantAbout";
import { TenantBranch, CreateTenantBranch } from "../Models/TenantBranch";
import { TenantContact } from "../Models/TenantContact";
import { TenantPreference } from "../Models/TenantPreference";
import { TenantShippingProviderConfig } from "../Models/TenantShippingProviderConfig";
import { TenantTechnicalAssistance } from "../Models/TenantTechnicalAssistance";
import { ProductCategory } from "../Models/ProductCategory";
import { SimplifiedUploadedFile } from "../Models/UploadedFile";
import { TenantContextType } from "../Types/TenantContextType";
import LoadingSpinner from "../Components/LoadingSpinner/LoadingSpinner";

type TenantProviderProps = {
  children: ReactNode;
};

export const TenantContext = createContext<TenantContextType>(
  {} as TenantContextType,
);

export const TenantProvider = ({ children }: TenantProviderProps) => {
  const [tenant, setTenant] = useState<Tenant>({} as Tenant);
  const [tenantAbout, setTenantAbout] = useState<TenantAbout>(
    {} as TenantAbout,
  );
  const [tenantContact, setTenantContact] = useState<TenantContact>(
    {} as TenantContact,
  );
  const [tenantBranches, setTenantBranches] = useState<TenantBranch[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [tenantPreference, setTenantPreference] = useState<TenantPreference>(
    {} as TenantPreference,
  );
  const [tenantShippingProviderConfig, setTenantShippingProviderConfig] =
    useState<TenantShippingProviderConfig | null>(null);
  const [tenantTechnicalAssistance, setTenantTechnicalAssistance] =
    useState<TenantTechnicalAssistance | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const logError = (...args: unknown[]) => {
    if (process.env.NODE_ENV === "development") {
      console.error(...args);
    }
  };

  const fetchTenant = async () => {
    try {
      const response = await tenantService.get();
      setTenant(response);
    } catch (err) {
      logError("Error fetching tenant", err);
    }
  };

  const fetchTenantAbout = async () => {
    try {
      const response = await tenantAboutService.get();
      setTenantAbout(response);
    } catch (err) {
      logError("Error fetching tenant about", err);
    }
  };

  const fetchTenantContact = async () => {
    try {
      const response = await tenantContactService.get();
      setTenantContact(response);
    } catch (err) {
      logError("Error fetching tenant contact", err);
    }
  };

  const fetchTenantBranches = async () => {
    try {
      const response = await tenantBranchService.getAll();
      setTenantBranches(response ?? []);
    } catch (err) {
      logError("Error fetching tenant branches", err);
    }
  };

  const fetchTenantPreference = async (): Promise<TenantPreference | null> => {
    try {
      const response = await tenantPreferenceService.get();
      setTenantPreference(response);
      return response;
    } catch (err) {
      logError("Error fetching tenant preference", err);
      return null;
    }
  };

  const fetchTenantShippingProviderConfig =
    async (): Promise<TenantShippingProviderConfig | null> => {
      try {
        const response =
          await tenantShippingProviderConfigService.getPublicCorreios();
        setTenantShippingProviderConfig(response);
        return response;
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setTenantShippingProviderConfig(null);
          return null;
        }

        logError("Error fetching tenant shipping provider config", err);
        return null;
      }
    };

  const fetchTenantTechnicalAssistance =
    async (): Promise<TenantTechnicalAssistance | null> => {
      if (tenantPreference?.enableTechnicalAssistancePage === false) {
        setTenantTechnicalAssistance(null);
        return null;
      }
      try {
        const response = await tenantTechnicalAssistanceService.get();
        setTenantTechnicalAssistance(response);
        return response;
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setTenantTechnicalAssistance(null);
          return null;
        } else {
          logError("Error fetching tenant technical assistance", err);
          return null;
        }
      }
    };

  useEffect(() => {
    // Single aggregated request instead of seven, collapsing the first-load
    // round trips. Falls back to the individual fetches if bootstrap fails, so a
    // problem with the aggregate endpoint never blanks the whole storefront.
    const fetchAllIndividually = async () => {
      const [, , , , , , cats] = await Promise.all([
        fetchTenant(),
        fetchTenantAbout(),
        fetchTenantContact(),
        fetchTenantBranches(),
        fetchTenantPreference(),
        fetchTenantShippingProviderConfig(),
        productCategoryService.getAll().catch(() => [] as ProductCategory[]),
      ]);
      setCategories(cats);
    };

    const fetchAll = async () => {
      setLoading(true);
      try {
        const bootstrap = await tenantService.getBootstrap();
        setTenant(bootstrap.tenant ?? ({} as Tenant));
        setTenantAbout(bootstrap.about ?? ({} as TenantAbout));
        setTenantContact(bootstrap.contact ?? ({} as TenantContact));
        setTenantBranches(bootstrap.branches ?? []);
        setTenantPreference(bootstrap.preference ?? ({} as TenantPreference));
        setTenantShippingProviderConfig(bootstrap.shippingProviderConfig ?? null);
        setCategories(bootstrap.categories ?? []);
      } catch (err) {
        logError("Tenant bootstrap failed, falling back to individual fetches", err);
        await fetchAllIndividually();
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadTechnicalAssistance = async () => {
      if (tenantPreference?.enableTechnicalAssistancePage === false) {
        setTenantTechnicalAssistance(null);
        return;
      }
      try {
        const response = await tenantTechnicalAssistanceService.get();
        setTenantTechnicalAssistance(response);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setTenantTechnicalAssistance(null);
        } else {
          logError("Error fetching tenant technical assistance", err);
        }
      }
    };

    if (tenantPreference?.enableTechnicalAssistancePage) {
      loadTechnicalAssistance();
    } else if (tenantPreference?.enableTechnicalAssistancePage === false) {
      setTenantTechnicalAssistance(null);
    }
  }, [tenantPreference?.enableTechnicalAssistancePage]);

  useEffect(() => {
    const normalizeHex = (value: string) => {
      const trimmed = value.trim();
      if (!trimmed.startsWith("#")) {
        return null;
      }
      let hex = trimmed.slice(1);
      if (hex.length === 3) {
        hex = hex
          .split("")
          .map((char) => char + char)
          .join("");
      }
      if (hex.length !== 6 || /[^0-9a-fA-F]/.test(hex)) {
        return null;
      }
      return `#${hex.toLowerCase()}`;
    };

    const hexToRgb = (hex: string) => {
      const normalized = hex.replace("#", "");
      return {
        r: parseInt(normalized.slice(0, 2), 16),
        g: parseInt(normalized.slice(2, 4), 16),
        b: parseInt(normalized.slice(4, 6), 16),
      };
    };

    const rgbToHex = (r: number, g: number, b: number) => {
      const toHex = (value: number) => value.toString(16).padStart(2, "0");
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    const mixColors = (baseHex: string, mixHex: string, baseWeight: number) => {
      const base = hexToRgb(baseHex);
      const mix = hexToRgb(mixHex);
      const r = Math.round(base.r * baseWeight + mix.r * (1 - baseWeight));
      const g = Math.round(base.g * baseWeight + mix.g * (1 - baseWeight));
      const b = Math.round(base.b * baseWeight + mix.b * (1 - baseWeight));
      return rgbToHex(r, g, b);
    };

    const primaryHex = tenantPreference?.primaryColor
      ? normalizeHex(tenantPreference.primaryColor)
      : null;

    if (primaryHex) {
      const root = document.documentElement.style;
      root.setProperty("--primary-color", primaryHex);
      root.setProperty("--primary-50", mixColors(primaryHex, "#ffffff", 0.05));
      root.setProperty("--primary-120", mixColors(primaryHex, "#ffffff", 0.12));
      root.setProperty("--primary-600", mixColors(primaryHex, "#000000", 0.95));
      root.setProperty("--primary-700", mixColors(primaryHex, "#000000", 0.75));
      root.setProperty(
        "--primary-border-600",
        mixColors(primaryHex, "#000000", 0.85),
      );
      root.setProperty(
        "--primary-border-700",
        mixColors(primaryHex, "#000000", 0.75),
      );
    }
  }, [tenantPreference?.primaryColor]);

  useEffect(() => {
    document.documentElement.lang = "pt-BR";
  }, []);

  useEffect(() => {
    document.documentElement.dataset.currency = "BRL";
  }, []);

  const updateTenant = async (data: UpdateTenant): Promise<boolean> => {
    try {
      const response = await tenantService.update(data);
      return response;
    } catch (err) {
      return false;
    }
  };

  const updateTenantFile = async (
    purpose: string,
    file: SimplifiedUploadedFile | null,
  ) => {
    setTenant((prev) => {
      switch (purpose) {
        case "Favicon":
          return { ...prev, favicon: file };
        case "Logo":
          return { ...prev, logo: file };
        case "BannerImage1":
          return { ...prev, bannerImageOne: file };
        case "BannerImage2":
          return { ...prev, bannerImageTwo: file };
        case "BannerImage3":
          return { ...prev, bannerImageThree: file };
        case "BannerImage4":
          return { ...prev, bannerImageFour: file };
        case "InitialImage1":
          return { ...prev, initialImageOne: file };
        case "InitialImage2":
          return { ...prev, initialImageTwo: file };
        default:
          return prev;
      }
    });
  };

  const updateTenantAbout = async (
    data: UpdateTenantAbout,
  ): Promise<boolean> => {
    try {
      const response = await tenantAboutService.update(data);
      return response;
    } catch (err) {
      return false;
    }
  };

  const updateTenantAboutFile = async (
    purpose: string,
    file: SimplifiedUploadedFile | null,
  ) => {
    setTenantAbout((prev) => {
      switch (purpose) {
        case "AboutImage1":
          return { ...prev, aboutImageOne: file };
        case "AboutImage2":
          return { ...prev, aboutImageTwo: file };
        default:
          return prev;
      }
    });
  };

  const updateTenantContact = async (data: TenantContact): Promise<boolean> => {
    try {
      const response = await tenantContactService.update(data);
      return response;
    } catch (err) {
      return false;
    }
  };

  const updateTenantPreference = async (
    data: TenantPreference,
  ): Promise<boolean> => {
    try {
      const response = await tenantPreferenceService.update(data);
      return response;
    } catch (err) {
      return false;
    }
  };

  const updateTenantTechnicalAssistance = async (
    data: TenantTechnicalAssistance,
  ): Promise<boolean> => {
    try {
      if (!data.id) {
        const { id, ...rest } = data;
        const createdId = await tenantTechnicalAssistanceService.create(rest);
        setTenantTechnicalAssistance({ ...data, id: createdId });
        return true;
      }

      const response = await tenantTechnicalAssistanceService.update(data);
      if (response) {
        setTenantTechnicalAssistance(data);
      }
      return response;
    } catch (err) {
      return false;
    }
  };

  const updateTenantTechnicalAssistanceFile = async (
    file: SimplifiedUploadedFile | null,
  ): Promise<void> => {
    setTenantTechnicalAssistance((prev) => {
      if (prev) {
        return { ...prev, image: file };
      }
      return prev;
    });
  };

  const createTenantBranch = async (
    data: CreateTenantBranch,
  ): Promise<string | null> => {
    try {
      const id = await tenantBranchService.create(data);
      await fetchTenantBranches();
      return id;
    } catch (err) {
      logError("Error creating tenant branch", err);
      return null;
    }
  };

  const updateTenantBranch = async (
    id: string,
    data: TenantBranch,
  ): Promise<boolean> => {
    try {
      const result = await tenantBranchService.update(id, data);
      await fetchTenantBranches();
      return result;
    } catch (err) {
      logError("Error updating tenant branch", err);
      return false;
    }
  };

  const deleteTenantBranch = async (id: string): Promise<boolean> => {
    try {
      const result = await tenantBranchService.delete(id);
      if (result) {
        setTenantBranches((prev) => prev.filter((b) => b.id !== id));
      }
      return result;
    } catch (err) {
      logError("Error deleting tenant branch", err);
      return false;
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <TenantContext.Provider
      value={{
        tenant,
        categories,
        tenantAbout,
        tenantContact,
        tenantBranches,
        tenantPreference,
        tenantShippingProviderConfig,
        tenantTechnicalAssistance,
        loading,
        fetchTenant,
        fetchTenantAbout,
        fetchTenantContact,
        fetchTenantBranches,
        fetchTenantPreference,
        fetchTenantShippingProviderConfig,
        fetchTenantTechnicalAssistance,
        updateTenant,
        updateTenantFile,
        updateTenantAbout,
        updateTenantAboutFile,
        updateTenantContact,
        updateTenantPreference,
        updateTenantTechnicalAssistance,
        updateTenantTechnicalAssistanceFile,
        createTenantBranch,
        updateTenantBranch,
        deleteTenantBranch,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};
